import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import {
  AdsGroupAd,
  KeywordConstraint,
  MarketingApproach,
  marketingApproaches,
} from '@betterfunnel/types';
import { DialogTitle, Disclosure, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import Lottie from 'lottie-react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { uid } from 'uid';
import { functions } from '../Firebase';
import languages from '../assets/languages.json';
import websiteBuildingAnimation from '../assets/lotties/website-building.json';
import KeywordsAutocomplete from '../components/KeywordsAutocomplete';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { useHasFeature } from '../hooks/useHasFeature';
import { suggestCampaignForAd } from '../services/DynamicPagesService';
import { camelCaseToTitle, classNames } from '../utils';
import { formFieldUrlValidator } from '../utils/validator';
import CreateFormModal from './CreateFormModal';

interface DynamicPageData {
  name?: string;
  path?: string;
  goal?: string;
  audience?: string;
  description?: string;
  appStoreLink?: string;
  playStoreLink?: string;
  link?: string;
  calendlyLink?: string;
  formId?: string;
  extraLink?: string;
  extraText?: string;
  language?: { name: string; code: string };
  domains?: string[];
  variants?: {
    id: string;
    name?: string;
    prompt?: string;
    keywords?: KeywordConstraint[];
    marketingApproach?: MarketingApproach;
  }[];
}

interface Props {
  adAccountsAd?: AdsGroupAd & { id: string };
  open: boolean;
  onClose: () => void;
}

export default function CreateDynamicPageModal({
  adAccountsAd,
  open,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const { show } = useContext(SnackbarContext);
  const { slug } = useParams();

  const { selectedAccount } = useContext(UserContext);
  const { forms, domains, primaryDomain } = useContext(DashboardContext);

  const [dataFromAd, setDataFromAd] = useState<Partial<DynamicPageData>>();
  const [loadingDataForAd, setLoadingDataForAd] = useState(false);
  const [dataForAdError, setDataForAdError] = useState<Error>();
  const [data, setData] = useState<DynamicPageData>({ path: uid(6) });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();
  const [createFormDialogOpen, setCreateFormDialogOpen] = useState(false);

  const campaignGoal = useMemo(
    () => data.goal ?? dataFromAd?.goal,
    [data, dataFromAd],
  );

  useEffect(() => {
    if (open) return;

    setData({ path: uid(6) });
    setCreating(false);
    setError(undefined);
    setDataFromAd(undefined);
    setLoadingDataForAd(false);
    setDataForAdError(undefined);
  }, [open]);

  const loadDataFromAd = useCallback(() => {
    if (!adAccountsAd || !selectedAccount) {
      setDataFromAd(undefined);
      return;
    }

    setLoadingDataForAd(true);
    setDataForAdError(undefined);
    suggestCampaignForAd(selectedAccount.id, adAccountsAd.id)
      .then((res) => {
        setDataFromAd({
          name: res.campaign.name,
          path: res.campaign.path,
          goal: res.campaign.goal,
          audience: res.campaign.audience,
          description: res.campaign.description,
          variants: res.variants.map((v) => ({ ...v, id: uid(8) })),
        });
      })
      .catch((e) => {
        setDataForAdError(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoadingDataForAd(false);
      });
  }, [selectedAccount, adAccountsAd]);

  useEffect(() => {
    if (dataFromAd) return;

    loadDataFromAd();
  }, [loadDataFromAd, dataFromAd]);

  const createCampaign = useCallback(async () => {
    if (creating) return;
    if (!primaryDomain) return;

    if (data.domains && !data.domains?.length) {
      show('Please select at least one domain');
      return;
    }

    setCreating(true);

    try {
      const fn = httpsCallable<any, any>(
        functions,
        'accounts-campaigns-createCampaign',
      );

      const res = await fn({
        ...dataFromAd,
        ...data,
        domains: data.domains ?? [primaryDomain.data().domain],
        accountId: selectedAccount?.id,
        adId: adAccountsAd?.id,
      });
      // await fetchCampaigns(firebaseUser?.uid ?? '');

      navigate(`/workspace/${slug}/pages/${res.data.campaignId}/variants`);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setCreating(false);
  }, [
    creating,
    primaryDomain,
    data,
    show,
    dataFromAd,
    selectedAccount?.id,
    adAccountsAd?.id,
    navigate,
    slug,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createCampaign(),
      },
    ],
    [creating, onClose, createCampaign],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actionsAlignment={adAccountsAd ? 'end' : 'stretch'}
      actions={actions}
      width={adAccountsAd ? '6xl' : 'sm'}
    >
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Create a new dynamic page
      </DialogTitle>
      {(loadingDataForAd || dataForAdError) && !dataFromAd ? (
        <div
          className={
            'absolute bottom-0 left-0 right-0 top-0 flex h-full flex-col items-center justify-center gap-8'
          }
        >
          {loadingDataForAd ? (
            <Lottie animationData={websiteBuildingAnimation} loop={true} />
          ) : (
            <></>
          )}
          {!loadingDataForAd && dataForAdError ? (
            <>
              <p className="mt-5 w-full text-center text-red-600">
                {dataForAdError.message}
              </p>
              <Button
                variant="ternary"
                size="small"
                onClick={() => loadDataFromAd()}
              >
                Try Again
              </Button>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      <div
        className={classNames(
          adAccountsAd && !dataFromAd ? 'pointer-events-none invisible' : '',
          'flex flex-col justify-stretch gap-4 sm:flex-row',
        )}
      >
        <div className="mt-4 flex flex-1 flex-col gap-4">
          <TextField
            title="Page name"
            readOnly={creating}
            value={data.name ?? dataFromAd?.name ?? ''}
            onChange={(e) => {
              setData((d) => {
                d.name = e.target.value;
                return { ...d };
              });
            }}
          />
          <TextField
            title="Path"
            readOnly={creating}
            value={data.path ?? dataFromAd?.path}
            onChange={(e) => {
              setData((d) => {
                d.path = e.target.value;
                return { ...d };
              });
            }}
          />
          <Select
            title="Page goal"
            placeholder="select your goal..."
            selected={data.goal ?? dataFromAd?.goal}
            disabled={creating}
            onChange={(e: string) => {
              setData((d) => {
                d.goal = e;
                return { ...d };
              });
            }}
            options={[
              {
                value: 'leads',
                child: 'Collect leads',
              },
              {
                value: 'appDownload',
                child: 'App download',
              },
              {
                value: 'linkClick',
                child: 'Link click',
              },
              {
                value: 'calendly',
                child: 'Meeting via Calendly',
              },
              {
                value: 'assetDownload',
                child: 'Asset download',
                disabled: true,
              },
              {
                value: 'demoRequest',
                child: 'Demo request',
                disabled: true,
              },
              {
                value: 'ecourseRegistration',
                child: 'ECourse registration',
                disabled: true,
              },
              {
                value: 'enrollment',
                child: 'Enrollment application',
                disabled: true,
              },
              {
                value: 'eventRegistration',
                child: 'Event registration',
                disabled: true,
              },
              {
                value: 'productSale',
                child: 'Product sales',
                disabled: true,
              },
              {
                value: 'scheduleAppointment',
                child: 'Schedule appointment',
                disabled: true,
              },
              {
                value: 'signup',
                child: 'Signup',
                disabled: true,
              },
              {
                value: 'webinar',
                child: 'Webinar registration',
                disabled: true,
              },
            ]}
          />
          {campaignGoal === 'appDownload' ? (
            <>
              <TextField
                title="App Store link"
                type="url"
                readOnly={creating}
                value={data.appStoreLink ?? dataFromAd?.appStoreLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.appStoreLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlValidator(
                  data.appStoreLink ?? dataFromAd?.appStoreLink ?? '',
                )}
              />
              <TextField
                title="Play Store link"
                type="url"
                readOnly={creating}
                value={data.playStoreLink ?? dataFromAd?.playStoreLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.playStoreLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlValidator(
                  data.playStoreLink ?? dataFromAd?.playStoreLink ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'linkClick' ? (
            <>
              <TextField
                title="Link"
                type="url"
                readOnly={creating}
                value={data.link ?? dataFromAd?.link ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.link = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlValidator(
                  data.link ?? dataFromAd?.link ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'calendly' ? (
            <>
              <TextField
                title="Calendly link"
                type="url"
                readOnly={creating}
                value={data.calendlyLink ?? dataFromAd?.calendlyLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.calendlyLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlValidator(
                  data.calendlyLink ?? dataFromAd?.calendlyLink ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'leads' ? (
            <Select
              title="Lead form"
              placeholder="Select a lead form.."
              selected={data.formId ?? dataFromAd?.formId ?? ''}
              disabled={creating}
              onChange={(e: string) => {
                if (e === 'new') {
                  setCreateFormDialogOpen(true);
                  return;
                }
                setData((d) => {
                  d.formId = e;
                  return { ...d };
                });
              }}
              loading={!forms}
              options={[
                ...(forms?.map((f) => ({
                  value: f.id,
                  child: f.data().name,
                })) ?? []),
                {
                  value: 'new',
                  child: 'Create new...',
                },
              ]}
            />
          ) : (
            <></>
          )}
          <TextField
            title="Audience"
            readOnly={creating}
            value={data.audience ?? dataFromAd?.audience ?? ''}
            onChange={(e) => {
              setData((d) => {
                d.audience = e.target.value;
                return { ...d };
              });
            }}
          />
          <div>
            <TextArea
              title="Page offering"
              readOnly={creating}
              value={data.description ?? dataFromAd?.description ?? ''}
              onChange={(e) => {
                setData((d) => {
                  d.description = e.target.value;
                  return { ...d };
                });
              }}
              rows={4}
              name="comment"
              id="comment"
            />
          </div>
          {primaryDomain?.data().type === 'custom' ? (
            <Select<string>
              multi
              selected={data.domains ?? [primaryDomain.data().domain]}
              loading={!domains}
              options={(domains ?? []).map((d) => ({
                value: d.data().domain,
                child: d.data().domain,
              }))}
              onChange={(selected) => {
                setData((d) => {
                  d.domains = selected;
                  return { ...d };
                });
              }}
            />
          ) : (
            <></>
          )}
          <AdvancedSettings
            creating={creating}
            data={data}
            dataFromAd={dataFromAd}
            setData={setData}
          />
        </div>

        {adAccountsAd ? (
          <>
            <div></div>
            <CampaignVariants
              creating={creating}
              adGroupId={adAccountsAd.adGroupId}
              data={data}
              dataFromAd={dataFromAd}
              setData={setData}
            />
          </>
        ) : (
          <></>
        )}
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
      <CreateFormModal
        open={createFormDialogOpen}
        onClose={() => {
          setCreateFormDialogOpen(false);
        }}
      />
    </Modal>
  );
}

function AdvancedSettings({
  creating,
  data,
  setData,
}: {
  creating: boolean;
  data: DynamicPageData;
  dataFromAd?: DynamicPageData;
  setData: Dispatch<SetStateAction<DynamicPageData>>;
}) {
  const hasExtraLinkFeature = useHasFeature(
    'dynamicPages:create:fields:extraLink',
  );

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <Disclosure.Button className="flex items-center gap-1 py-2">
            <Cog6ToothIcon className="h-4 w-4" />
            Advanced Settings
            <div className="flex-1" />
            <ChevronDownIcon
              className={classNames(
                'h-4 w-4',
                open ? 'rotate-180 transform' : '',
              )}
            />
          </Disclosure.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel>
              <div className="flex flex-col gap-2">
                <h3>Settings</h3>
                <Autocomplete
                  title="Language"
                  selected={
                    data?.language ??
                    languages.find((l) => l.code === 'en-US') ??
                    languages[0]
                  }
                  options={languages}
                  getValue={(o) => o.name}
                  getOptionId={(o) => o.code}
                  onChange={(selected) => {
                    setData((d: any) => {
                      d.language = selected;
                      return { ...d };
                    });
                  }}
                />
                {hasExtraLinkFeature && (
                  <>
                    <h3>Extra Information</h3>
                    <TextField
                      placeholder="link"
                      type="url"
                      readOnly={creating}
                      value={data.extraLink}
                      onChange={(e) => {
                        setData((d: any) => {
                          d.extraLink = e.target.value;
                          return { ...d };
                        });
                      }}
                      validation={formFieldUrlValidator(data.extraLink)}
                    />
                  </>
                )}
                <TextArea
                  id="extraText"
                  name="extraText"
                  placeholder="content"
                  rows={4}
                  value={data.extraText}
                  onChange={(e) => {
                    setData((d: any) => {
                      d.extraText = e.target.value;
                      return { ...d };
                    });
                  }}
                />
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}

type VariantData = NonNullable<DynamicPageData['variants']>[number];

function CampaignVariants({
  creating,
  adGroupId,
  data,
  dataFromAd,
  setData,
}: {
  creating: boolean;
  adGroupId: string;
  data: DynamicPageData;
  dataFromAd?: DynamicPageData;
  setData: Dispatch<SetStateAction<DynamicPageData>>;
}) {
  const variants = data.variants ?? dataFromAd?.variants ?? [];

  const keywordsFilter = useMemo(() => ({ adGroupId }), [adGroupId]);

  const update = useCallback(
    (newVariant: VariantData) => {
      setData((d) => {
        const newVariants = [...(d.variants ?? dataFromAd?.variants ?? [])];
        const existingIndex = newVariants.findIndex(
          (v) => v.id === newVariant.id,
        );
        if (existingIndex !== -1) {
          newVariants[existingIndex] = newVariant;
        } else {
          newVariants.push(newVariant);
        }

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  const duplicate = useCallback(
    (variant: VariantData) => {
      setData((d) => {
        const newVariants = [...(d.variants ?? dataFromAd?.variants ?? [])];
        const existingIndex = newVariants.findIndex((v) => v.id === variant.id);

        const newVariant = {
          ...variant,
          id: uid(8),
          name: [variant.name, 'Copy'].filter((x) => !!x).join(' '),
        };
        if (existingIndex !== -1) {
          newVariants.splice(existingIndex + 1, 0, newVariant);
        } else {
          newVariants.push(newVariant);
        }

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  const deleteVariant = useCallback(
    (variantId: string) => {
      setData((d) => {
        const newVariants = [
          ...(d.variants ?? dataFromAd?.variants ?? []),
        ].filter((v) => v.id !== variantId);

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  return (
    <div className="mt-4 flex flex-1 flex-col divide-y">
      <label
        htmlFor="comment"
        className="mb-2 block text-sm font-medium leading-6 text-gray-900"
      >
        Suggested variants
      </label>

      {variants.map((variant, i) => {
        const setData = (_update: (variant: VariantData) => VariantData) => {
          update(_update(variant));
        };
        return (
          <Disclosure key={variant.id}>
            {({ open }) => (
              <>
                <Disclosure.Button className="group flex items-center gap-1 py-3 hover:bg-gray-50">
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-sm">
                      #{i + 1} {variant.name || 'Variant'}
                    </span>

                    {
                      // eslint-disable-next-line no-constant-condition
                      false && !open && variant.keywords?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {variant.keywords
                            ?.slice(0, 3)
                            .map((keyword) => (
                              <Badge
                                key={keyword.keyword}
                                title={keyword.keyword}
                                rounded={false}
                                hideDot
                                showBorder
                                size="small"
                              />
                            ))}
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  </div>

                  <div className="flex-1" />

                  <IconButton
                    variant="ternary"
                    className={classNames(
                      open ? '' : 'hidden',
                      '-my-2 group-hover:block',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicate(variant);
                    }}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </IconButton>

                  <IconButton
                    variant="ternary"
                    className={classNames(
                      open ? '' : 'hidden',
                      '-my-2 group-hover:block',
                    )}
                    color="negative"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteVariant(variant.id);
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </IconButton>

                  <IconButton variant="ternary" className="-my-2">
                    <ChevronDownIcon
                      className={classNames(
                        'h-4 w-4',
                        open ? 'rotate-180 transform' : '',
                      )}
                    />
                  </IconButton>
                </Disclosure.Button>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Disclosure.Panel>
                    <div className="flex flex-col gap-2 py-2">
                      <TextField
                        title="Name"
                        readOnly={creating}
                        value={variant.name}
                        onChange={(e) => {
                          setData((d) => {
                            d.name = e.target.value;
                            return { ...d };
                          });
                        }}
                      />
                      <Select
                        title="Marketing approach"
                        placeholder="select your approach..."
                        selected={variant.marketingApproach}
                        disabled={creating}
                        onChange={(e) => {
                          setData((d) => {
                            d.marketingApproach = e;
                            return { ...d };
                          });
                        }}
                        options={marketingApproaches.map((approach) => ({
                          value: approach,
                          child: camelCaseToTitle(approach),
                        }))}
                      />
                      <KeywordsAutocomplete
                        filter={keywordsFilter}
                        selected={variant.keywords || []}
                        onChange={(newKeywords) => {
                          setData((d) => {
                            d.keywords = newKeywords;
                            return { ...d };
                          });
                        }}
                      />
                      <TextArea
                        id="prompt"
                        name="prompt"
                        title="Prompt (optional)"
                        placeholder="Optional constraints for the variant creation"
                        rows={4}
                        value={variant.prompt}
                        onChange={(e) => {
                          setData((d) => {
                            d.prompt = e.target.value;
                            return { ...d };
                          });
                        }}
                      />
                    </div>
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        );
      })}
      <div className="flex justify-end pt-3">
        <Button
          variant="ternary"
          size="x-small"
          icon={<PlusIcon className="h-4 w-4" />}
          onClick={() => {
            update({ id: uid(8) });
          }}
        >
          Add Variant
        </Button>
      </div>
    </div>
  );
}
